import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import traineegoAppTIcon from "../../assets/images/favicon-new-2026.ico";
import traineegoAppIcon from "../../assets/images/logo.png";
import { useTranslate } from "../../context/TranslationContext";
import "./HomeScreenGuidance.css";

const DISMISSAL_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

let globalDeferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    globalDeferredPrompt = e;
    window.dispatchEvent(new Event("traineegoBeforeInstallPrompt"));
  });

  // Developer / testing helper to test the prompt anytime in Console:
  window.resetPwaGuidance = () => {
    localStorage.removeItem("traineego_pwa_dismissed_at");
    localStorage.removeItem("traineego_pwa_installed");
    sessionStorage.removeItem("traineego_pwa_minimized");
    window.dispatchEvent(new Event("traineegoResetGuidance"));
    console.log("Traineego PWA guidance flags reset.");
  };
}

const HomeScreenGuidance = () => {
  const { t } = useTranslate("pwaGuidance");
  const { user } = useSelector((state) => state.authSlice || {});
  const location = useLocation();

  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(globalDeferredPrompt);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [showAndroidTip, setShowAndroidTip] = useState(false);

  // Platform detection
  const isIOS =
    typeof navigator !== "undefined" &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 1 &&
        /Macintosh/.test(navigator.userAgent))) &&
    !window.MSStream;

  const isAndroid =
    typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

  const checkShouldDisplay = useCallback(() => {
    if (typeof window === "undefined") return false;

    // 1. Check if running as standalone installed PWA
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true ||
      document.referrer.includes("android-app://");

    if (isStandalone) {
      return false;
    }

    // 2. Check if already marked as installed
    const alreadyInstalled =
      localStorage.getItem("traineego_pwa_installed") === "true";
    if (alreadyInstalled) {
      return false;
    }

    // 3. Check dismissal cooldown (7 days)
    const dismissedAt = localStorage.getItem("traineego_pwa_dismissed_at");
    if (
      dismissedAt &&
      Date.now() - parseInt(dismissedAt, 10) < DISMISSAL_COOLDOWN_MS
    ) {
      return false;
    }

    // 4. Do not show while country selection banner is active on home page
    const selectedCountry = localStorage.getItem("selectedCountry");
    const userCountry = user?.country;
    const isHome = location.pathname === "/" || location.pathname === "";

    if (isHome && !selectedCountry && !userCountry) {
      // Country popup is active on Home; wait until user enters platform
      return false;
    }

    return true;
  }, [user, location.pathname]);

  useEffect(() => {
    const handlePrompt = () => {
      setDeferredPrompt(globalDeferredPrompt);
    };

    const handleInstalled = () => {
      localStorage.setItem("traineego_pwa_installed", "true");
      setIsVisible(false);
      setIsMinimized(false);
    };

    const handleCountrySelected = () => {
      setTimeout(() => {
        if (checkShouldDisplay()) {
          const wasMinimized =
            sessionStorage.getItem("traineego_pwa_minimized") === "true";
          if (wasMinimized) {
            setIsMinimized(true);
          } else {
            setIsVisible(true);
          }
        }
      }, 1000);
    };

    const handleReset = () => {
      if (checkShouldDisplay()) {
        setIsMinimized(false);
        setIsVisible(true);
      }
    };

    window.addEventListener("traineegoBeforeInstallPrompt", handlePrompt);
    window.addEventListener("appinstalled", handleInstalled);
    window.addEventListener("traineegoCountrySelected", handleCountrySelected);
    window.addEventListener("traineegoResetGuidance", handleReset);
    window.addEventListener("storage", () => {
      if (checkShouldDisplay()) {
        const wasMinimized =
          sessionStorage.getItem("traineego_pwa_minimized") === "true";
        if (wasMinimized) {
          setIsMinimized(true);
        } else {
          setIsVisible(true);
        }
      }
    });

    // Initial check on mount or page navigation
    const timer = setTimeout(() => {
      if (checkShouldDisplay()) {
        const wasMinimized =
          sessionStorage.getItem("traineego_pwa_minimized") === "true";
        if (wasMinimized) {
          setIsMinimized(true);
        } else {
          setIsVisible(true);
        }
      }
    }, 1200);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("traineegoBeforeInstallPrompt", handlePrompt);
      window.removeEventListener("appinstalled", handleInstalled);
      window.removeEventListener(
        "traineegoCountrySelected",
        handleCountrySelected,
      );
      window.removeEventListener("traineegoResetGuidance", handleReset);
    };
  }, [checkShouldDisplay]);

  // X button minimizes the dialog into a floating badge so user can reopen it anytime on this page
  const handleMinimize = () => {
    setIsVisible(false);
    setIsMinimized(true);
    sessionStorage.setItem("traineego_pwa_minimized", "true");
  };

  // Reopen when clicking minimized badge
  const handleReopen = () => {
    setIsMinimized(false);
    setIsVisible(true);
    sessionStorage.removeItem("traineego_pwa_minimized");
  };

  // "Maybe later" closes and sets the 7-day cooldown
  const handleMaybeLater = () => {
    localStorage.setItem("traineego_pwa_dismissed_at", String(Date.now()));
    sessionStorage.removeItem("traineego_pwa_minimized");
    setIsVisible(false);
    setIsMinimized(false);

    // Show confirmation notice
    const message =
      t("remindLaterMessage") || "Got it! We'll remind you again in 7 days.";
    if (toast && typeof toast.info === "function") {
      toast.info(message);
    }
  };

  const handleInstallClick = async () => {
    const promptEvent = deferredPrompt || globalDeferredPrompt;

    if (promptEvent) {
      promptEvent.prompt();
      const choiceResult = await promptEvent.userChoice;
      if (choiceResult.outcome === "accepted") {
        localStorage.setItem("traineego_pwa_installed", "true");
        setIsVisible(false);
        setIsMinimized(false);
      }
      setDeferredPrompt(null);
      globalDeferredPrompt = null;
    } else {
      // If browser doesn't expose prompt or already fired, show instructional tip
      setShowAndroidTip((prev) => !prev);
    }
  };

  // If minimized, show floating badge button
  if (isMinimized) {
    return (
      <div className="traineego-pwa-minimized-badge-container">
        <button
          type="button"
          className="traineego-pwa-minimized-badge"
          onClick={handleReopen}
          aria-label="Open App Installation Guidance"
        >
          <img
            src={traineegoAppTIcon}
            alt="Traineego"
            className="traineego-pwa-minimized-icon"
          />
          <span className="traineego-pwa-minimized-text">
            {t("installApp") || "Install App"}
          </span>
          <i className="fa-solid fa-download traineego-pwa-minimized-arrow"></i>
        </button>
      </div>
    );
  }

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="traineego-pwa-guidance-container"
      role="dialog"
      aria-label="Add Traineego to your Home Screen"
    >
      <div className="traineego-pwa-card">
        {/* Minimize Button (X) */}
        <button
          type="button"
          className="traineego-pwa-close-btn"
          onClick={handleMinimize}
          title="Minimize"
          aria-label="Minimize"
        >
          <i className="fa-solid fa-xmark"></i>
        </button>

        {/* Header with App Logo */}
        <div className="traineego-pwa-header">
          <img
            src={traineegoAppIcon}
            alt="Traineego App Icon"
            className="traineego-pwa-app-icon"
          />
          <h3 className="traineego-pwa-title">
            {t("title") || "Add Traineego to your Home Screen"}
          </h3>
          <p className="traineego-pwa-subtitle">
            {t("subtitle") ||
              "Open Traineego like an app and access your Tracks faster."}
          </p>
        </div>

        {/* Platform Guidance Boxes */}
        <div className="traineego-pwa-sections">
          {/* iPhone / Safari Section */}
          <div
            className={`traineego-pwa-box ${isIOS ? "active-platform" : ""}`}
          >
            <div className="traineego-pwa-box-header">
              <span className="traineego-pwa-platform-icon apple">
                <i className="fa-brands fa-apple"></i>
              </span>
              <span className="traineego-pwa-box-title">
                {t("iosTitle") || "iPhone / Safari"}
              </span>
              {isIOS && (
                <span className="traineego-pwa-badge">Your Device</span>
              )}
            </div>

            <div className="traineego-pwa-steps">
              <div className="traineego-pwa-step-item">
                <span className="traineego-pwa-step-num">1</span>
                <span>
                  Tap <strong>Share</strong> in Safari
                  <i className="fa-solid fa-arrow-up-from-bracket traineego-pwa-inline-icon"></i>
                </span>
              </div>
              <div className="traineego-pwa-step-item">
                <span className="traineego-pwa-step-num">2</span>
                <span>
                  Select <strong>Add to Home Screen</strong>
                  <i className="fa-regular fa-square-plus traineego-pwa-inline-icon"></i>
                </span>
              </div>
              <div className="traineego-pwa-step-item">
                <span className="traineego-pwa-step-num">3</span>
                <span>
                  Tap <strong>Add</strong>
                </span>
              </div>
            </div>

            <button
              type="button"
              className="traineego-pwa-show-how-btn mt-2"
              onClick={() => setShowIosGuide((prev) => !prev)}
            >
              <span>{t("showMeHow") || "Show me how"}</span>
              <i
                className={`fa-solid fa-chevron-${showIosGuide ? "up" : "down"}`}
                style={{ fontSize: "10px" }}
              ></i>
            </button>

            {showIosGuide && (
              <div className="traineego-pwa-guide-tip">
                {t("iosTip") ||
                  "Tap the Share icon in Safari's bottom bar, scroll down the options list and select 'Add to Home Screen'."}
              </div>
            )}
          </div>

          {/* Android / Chrome Section */}
          <div
            className={`traineego-pwa-box ${isAndroid ? "active-platform" : ""}`}
          >
            <div className="traineego-pwa-box-header">
              <span className="traineego-pwa-platform-icon android">
                <i className="fa-brands fa-android"></i>
              </span>
              <span className="traineego-pwa-box-title">
                {t("androidTitle") || "Android / Chrome"}
              </span>
              {isAndroid && (
                <span className="traineego-pwa-badge">Your Device</span>
              )}
            </div>

            <button
              type="button"
              className="traineego-pwa-install-btn site-btn w-100"
              onClick={handleInstallClick}
            >
              <i className="fa-solid fa-download"></i>{" "}
              <span>{t("installBtn") || "Install Traineego"}</span>
            </button>

            {showAndroidTip && (
              <div className="traineego-pwa-guide-tip">
                {t("androidTip") ||
                  "Tap the 3 dots (⋮) in Chrome's top right corner and select 'Install app' or 'Add to Home screen'."}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="traineego-pwa-actions">
          <button
            type="button"
            className="site-btn border-btn"
            onClick={handleMaybeLater}
          >
            {t("maybeLater") || "Maybe later"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomeScreenGuidance;
